import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getTokens } from "../utils/auth";
import type { ContactSubmissionListResponse } from "../api/generated/types.gen";
import {
  listContactSubmissionsAdminApiAdminContactSubmissionsGet,
  markContactSubmissionViewedApiAdminContactSubmissionsSubmissionIdViewedPatch,
  markContactSubmissionUnviewedApiAdminContactSubmissionsSubmissionIdUnviewedPatch,
  getUnviewedSubmissionsCountApiAdminContactSubmissionsUnviewedCountGet,
} from "../api/generated";

/**
 * Hook for fetching contact submissions (admin only).
 */
export function useContactSubmissions() {
  const { accessToken } = getTokens();
  const isDevelopment = process.env.NODE_ENV === "development";

  return useQuery<ContactSubmissionListResponse>({
    queryKey: ["admin", "contact-submissions"],
    queryFn: async () => {
      // In development, allow requests even without token (DISABLE_AUTH mode)
      if (!accessToken && !isDevelopment) {
        throw new Error("No access token found. Please log in.");
      }

      const response = await listContactSubmissionsAdminApiAdminContactSubmissionsGet();

      if (!response.data) {
        if (response.error) {
          throw new Error(
            `Failed to fetch contact submissions: ${JSON.stringify(response.error)}`
          );
        }
        throw new Error("Failed to fetch contact submissions: No data returned");
      }

      return response.data;
    },
    enabled: !!accessToken || isDevelopment,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

/**
 * Hook for marking a contact submission as viewed (admin only).
 */
export function useMarkSubmissionViewed() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = getTokens();
  const isDevelopment = process.env.NODE_ENV === "development";

  return useMutation({
    mutationFn: async (submissionId: string) => {
      if (!accessToken && !isDevelopment) {
        throw new Error("No access token found. Please log in.");
      }

      const response =
        await markContactSubmissionViewedApiAdminContactSubmissionsSubmissionIdViewedPatch({
          path: { submission_id: submissionId },
        });

      if (!response.data) {
        if (response.error) {
          throw new Error(
            `Failed to mark submission as viewed: ${JSON.stringify(response.error)}`
          );
        }
        throw new Error("Failed to mark submission as viewed: No data returned");
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the submissions list and unviewed count
      queryClient.invalidateQueries({ queryKey: ["admin", "contact-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "contact-submissions", "unviewed-count"] });
      enqueueSnackbar("Submission marked as viewed", { variant: "success" });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.message || "Failed to mark submission as viewed",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for marking a contact submission as unviewed (admin only).
 */
export function useMarkSubmissionUnviewed() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = getTokens();
  const isDevelopment = process.env.NODE_ENV === "development";

  return useMutation({
    mutationFn: async (submissionId: string) => {
      if (!accessToken && !isDevelopment) {
        throw new Error("No access token found. Please log in.");
      }

      const response =
        await markContactSubmissionUnviewedApiAdminContactSubmissionsSubmissionIdUnviewedPatch({
          path: { submission_id: submissionId },
        });

      if (!response.data) {
        if (response.error) {
          throw new Error(
            `Failed to mark submission as unviewed: ${JSON.stringify(response.error)}`
          );
        }
        throw new Error("Failed to mark submission as unviewed: No data returned");
      }

      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the submissions list and unviewed count
      queryClient.invalidateQueries({ queryKey: ["admin", "contact-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "contact-submissions", "unviewed-count"] });
      enqueueSnackbar("Submission marked as unviewed", { variant: "success" });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.message || "Failed to mark submission as unviewed",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for fetching the count of unviewed contact submissions (admin only).
 */
export function useUnviewedSubmissionsCount() {
  const { accessToken } = getTokens();
  const isDevelopment = process.env.NODE_ENV === "development";

  return useQuery<number>({
    queryKey: ["admin", "contact-submissions", "unviewed-count"],
    queryFn: async () => {
      if (!accessToken && !isDevelopment) {
        throw new Error("No access token found. Please log in.");
      }

      const response =
        await getUnviewedSubmissionsCountApiAdminContactSubmissionsUnviewedCountGet();

      if (!response.data) {
        if (response.error) {
          throw new Error(
            `Failed to fetch unviewed count: ${JSON.stringify(response.error)}`
          );
        }
        throw new Error("Failed to fetch unviewed count: No data returned");
      }

      return (response.data as { unviewed_count: number }).unviewed_count;
    },
    enabled: !!accessToken || isDevelopment,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
    retry: 1,
  });
}
